import { ThemeToggle } from '@/components/theme-toggle';
import { Button } from '@/components/ui/button';
import { SearchBar } from '@/components/ui/custom/SearchBar';
import { useArtifactStore } from '@/store/artifactStore';
import { useUIStore } from '@/store/uiStore';
import { DEFAULT_HTML_TEMPLATE } from '@/utils/constants';
import { Link, useRouter, useRouterState } from '@tanstack/react-router';
import { ArrowLeft, Plus } from 'lucide-react';
import React, { useCallback } from 'react';

interface TopBarProps {
  className?: string;
}

export const TopBar: React.FC<TopBarProps> = ({ className }) => {
  const {
    selectedArtifactId,
    searchQuery,
    setSearchQuery,
  } = useUIStore();

  const {
    createArtifact,
    artifacts,
    isLoading,
  } = useArtifactStore();

  const router = useRouter();
  const routerState = useRouterState();

  const currentArtifact = selectedArtifactId
    ? artifacts.find(a => a.id === selectedArtifactId)
    : null;

  const handleCreateArtifact = useCallback(async () => {
    try {
      const newArtifact = await createArtifact({
        title: 'New Artifact',
        htmlContent: DEFAULT_HTML_TEMPLATE,
      });
      router.navigate({ to: `/editor/${newArtifact.id}` });
    } catch (error) {
      console.error('Failed to create artifact:', error);
    }
  }, [createArtifact, router]);

  return (
    <header className={`border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60 px-4 ${className || ''}`}>
      <div className="flex items-center justify-center h-16">
        <div className="flex items-center justify-between w-full max-w-7xl">
          {/* Left side - Logo/Navigation */}
          <div className="flex items-center gap-4">
            <Link to="/" className="text-xl font-bold text-foreground hover:text-primary transition-colors">
              Artifacts
            </Link>

            {routerState.location.pathname.startsWith('/editor/') && currentArtifact && (
              <div className="flex items-center gap-2">
                <Link to="/">
                  <Button variant="ghost" size="sm" className="gap-2">
                    <ArrowLeft className="h-4 w-4" />
                    <span className="hidden sm:inline">Back to Artifacts</span>
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Center - Search (only on home page) */}
          {routerState.location.pathname === '/' && (
            <div className="flex-1 max-w-md mx-4">
              <SearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search artifacts..."
              />
            </div>
          )}

          {/* Right side - Actions and Theme */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <ThemeToggle />

            <Button
              onClick={handleCreateArtifact}
              disabled={isLoading}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Create New</span>
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
}; 